import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, FileText, Download, Trash2, Eye, Calendar, X, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import mammoth from 'mammoth';

interface ResumeVersionsProps {
  candidateId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ResumeVersions = ({ candidateId, open, onOpenChange }: ResumeVersionsProps) => {
  const [resumes, setResumes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [viewingResume, setViewingResume] = useState<{ 
    url: string; 
    filename: string; 
    type: 'pdf' | 'docx' | 'other';
    htmlContent?: string;
  } | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open && candidateId) {
      fetchResumes();
    }
  }, [open, candidateId]);

  // Clean up viewing state when dialog closes
  useEffect(() => {
    if (!open) {
      setViewingResume(null);
    }
  }, [open]);

  const fetchResumes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('candidate_resumes')
        .select('*')
        .eq('candidate_id', candidateId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setResumes(data || []);
    } catch (error) {
      console.error('Error fetching resumes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (resume: any) => {
    try {
      // First try to create a signed URL for download
      const { data: signedData, error: signedError } = await supabase.storage
        .from('resumes')
        .createSignedUrl(resume.storage_path, 60);

      if (signedError) {
        console.error('Signed URL error:', signedError);
        throw signedError;
      }

      if (signedData?.signedUrl) {
        // Use fetch to download the file
        const response = await fetch(signedData.signedUrl);
        if (!response.ok) throw new Error('Failed to download file');
        
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = resume.original_filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast({ title: 'Download started' });
      }
    } catch (error: any) {
      console.error('Download error:', error);
      toast({ title: 'Download failed', description: error.message || 'Unable to download file', variant: 'destructive' });
    }
  };

  const handleDelete = async (resumeId: string, storagePath: string) => {
    if (!confirm('Are you sure you want to delete this resume version?')) return;

    setDeleting(resumeId);
    try {
      // Delete from storage
      await supabase.storage.from('resumes').remove([storagePath]);
      
      // Delete record
      const { error } = await supabase
        .from('candidate_resumes')
        .delete()
        .eq('id', resumeId);

      if (error) throw error;

      toast({ title: 'Resume deleted successfully' });
      fetchResumes();
    } catch (error: any) {
      toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  const getFileType = (filename: string): 'pdf' | 'docx' | 'other' => {
    const lower = filename.toLowerCase();
    if (lower.endsWith('.pdf')) return 'pdf';
    if (lower.endsWith('.docx') || lower.endsWith('.doc')) return 'docx';
    return 'other';
  };

  const handleView = async (resume: any) => {
    setViewLoading(true);
    try {
      const fileType = getFileType(resume.original_filename);

      // Create a signed URL for the file (valid for 1 hour)
      const { data: signedData, error: signedError } = await supabase.storage
        .from('resumes')
        .createSignedUrl(resume.storage_path, 3600);

      if (signedError) {
        console.error('Signed URL error:', signedError);
        throw signedError;
      }

      if (!signedData?.signedUrl) {
        throw new Error('Could not generate file URL');
      }

      // Fetch the file and create a blob URL to avoid Chrome blocking
      const response = await fetch(signedData.signedUrl);
      if (!response.ok) throw new Error('Failed to fetch file');
      
      const blob = await response.blob();

      if (fileType === 'pdf') {
        // For PDF, create a blob URL to avoid Chrome blocking
        const blobUrl = URL.createObjectURL(blob);
        setViewingResume({ 
          url: blobUrl, 
          filename: resume.original_filename, 
          type: 'pdf' 
        });
      } else if (fileType === 'docx') {
        // For DOCX, convert to HTML using mammoth
        const arrayBuffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setViewingResume({ 
          url: signedData.signedUrl, 
          filename: resume.original_filename, 
          type: 'docx',
          htmlContent: result.value
        });
      } else {
        // For other files, just set the URL for download
        setViewingResume({ 
          url: signedData.signedUrl, 
          filename: resume.original_filename, 
          type: 'other' 
        });
      }
    } catch (error: any) {
      console.error('View error:', error);
      toast({ title: 'Unable to view', description: error.message || 'File not found or access denied', variant: 'destructive' });
    } finally {
      setViewLoading(false);
    }
  };

  const handleOpenInNewTab = () => {
    if (viewingResume?.url) {
      window.open(viewingResume.url, '_blank');
    } else if (viewingResume?.htmlContent) {
      // Open HTML content in new tab for DOCX
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>${viewingResume.filename}</title>
            <style>
              body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.6; }
              h1, h2, h3 { color: #333; }
              p { margin: 1em 0; }
              ul, ol { margin: 1em 0; padding-left: 2em; }
              table { border-collapse: collapse; width: 100%; margin: 1em 0; }
              td, th { border: 1px solid #ddd; padding: 8px; text-align: left; }
            </style>
          </head>
          <body>${viewingResume.htmlContent}</body>
          </html>
        `);
        newWindow.document.close();
      }
    }
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return 'Unknown size';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={viewingResume ? "max-w-5xl max-h-[90vh]" : "max-w-2xl"}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>
              {viewingResume ? viewingResume.filename : 'Resume Versions'}
            </DialogTitle>
            {viewingResume && (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={handleOpenInNewTab}>
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Open in new tab
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setViewingResume(null)}>
                  <X className="h-4 w-4 mr-1" />
                  Back to list
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        {viewingResume ? (
          <div className="w-full h-[70vh] rounded-lg overflow-hidden border bg-muted">
            {viewingResume.type === 'pdf' ? (
              <iframe
                src={`${viewingResume.url}#toolbar=1&navpanes=0`}
                className="w-full h-full"
                title="Resume Viewer"
              />
            ) : viewingResume.type === 'docx' && viewingResume.htmlContent ? (
              <div 
                className="w-full h-full overflow-auto bg-white p-8"
                dangerouslySetInnerHTML={{ __html: viewingResume.htmlContent }}
                style={{
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: 1.6,
                  color: '#333'
                }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-4">
                <FileText className="h-16 w-16 text-muted-foreground" />
                <p className="text-muted-foreground">
                  Preview not available for this file type
                </p>
                <Button onClick={handleOpenInNewTab}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in new tab
                </Button>
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : resumes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No resumes uploaded yet</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {resumes.map((resume, index) => (
              <Card key={resume.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-8 w-8 text-primary" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{resume.original_filename}</p>
                        {index === 0 && (
                          <Badge variant="secondary" className="text-xs">Latest</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {resume.uploaded_at && format(new Date(resume.uploaded_at), 'MMM dd, yyyy HH:mm')}
                        </span>
                        <span>{formatFileSize(resume.file_size)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleView(resume)}
                      disabled={viewLoading}
                    >
                      {viewLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDownload(resume)}>
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(resume.id, resume.storage_path)}
                      disabled={deleting === resume.id}
                    >
                      {deleting === resume.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-destructive" />
                      )}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ResumeVersions;
